/**
 * The fetch wrapper: access token in memory, refresh token in an httpOnly cookie,
 * silent refresh, and the per-tab identity pin.
 *
 * Ported from UniTicket (`packages/frontend/src/lib/api-client.ts`) with the reasoning
 * intact. Every guard here exists because of something that went wrong there; none of it
 * should be re-derived.
 *
 * ⚠ The access token is deliberately a module variable and NOT in localStorage or
 * sessionStorage. Anything readable from JavaScript is readable by injected JavaScript;
 * keeping it in a closure means a token dies with the tab and never sits at rest.
 */

import type { ApiErrorBody, RefreshResponse } from "@cocon-stats/shared";

let accessToken: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

export const API_PREFIX = "/internal/v1";

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public details?: string[],
  ) {
    super(message);
    this.name = "ApiError";
  }
}

// ── Per-tab session identity pin ──────────────────────────────
//
// The refresh cookie is shared by ALL TABS of the same origin, while the access token is
// per tab and in memory. Without a guard, a silent refresh — on reload, or when the access
// token expires — reads that shared cookie and transparently switches this tab to whoever
// signed in last in another tab.
//
// ⚠ In this product that is not a cosmetic bug. Ubi signing in beside a contractor, or one
// contractor beside another, must never end with one of them looking at the other's rows
// under their own name. The whole point of Cocon-Stats is that everyone sees the same
// number for the same day — not that anyone sees someone else's.
//
// So the authenticated user's id is pinned in sessionStorage, which is per tab, and a
// refresh that resolves to a different identity is refused rather than adopted.

const SESSION_UID_KEY = "cocon_stats_session_uid";

export function pinSessionUser(userId: string): void {
  try {
    sessionStorage.setItem(SESSION_UID_KEY, userId);
  } catch {
    // Unavailable in private mode or with site data blocked. The guard degrades to a
    // no-op rather than breaking sign-in.
  }
}

export function getPinnedSessionUser(): string | null {
  try {
    return sessionStorage.getItem(SESSION_UID_KEY);
  } catch {
    return null;
  }
}

export function clearPinnedSessionUser(): void {
  try {
    sessionStorage.removeItem(SESSION_UID_KEY);
  } catch {
    /* ignore */
  }
}

/** Read the `sub` claim without verifying. Verification is the server's job, always. */
function decodeJwtSub(token: string): string | null {
  try {
    const part = token.split(".")[1];
    if (!part) return null;
    let b64 = part.replace(/-/g, "+").replace(/_/g, "/");
    while (b64.length % 4) b64 += "=";
    const json = JSON.parse(atob(b64)) as { sub?: unknown };
    return typeof json.sub === "string" ? json.sub : null;
  } catch {
    return null;
  }
}

// ── Handlers the auth layer registers ─────────────────────────

/** A silent refresh resolved to a different identity than this tab is pinned to. */
let onSessionConflict: (() => void) | null = null;
export function setSessionConflictHandler(handler: (() => void) | null): void {
  onSessionConflict = handler;
}

/**
 * A request 401'd AND the origin answered 401 to /auth/refresh — the shared cookie no
 * longer yields a session (expired, revoked by a sign-out or password change in another
 * tab, or the account was deactivated).
 *
 * ⚠ NOT invoked for an identity conflict, and not for transient failures (429, 5xx,
 * network). Treating a 429 as "signed out" would throw people back to the login screen
 * because they clicked twice.
 */
let onSessionExpired: (() => void) | null = null;
export function setSessionExpiredHandler(handler: (() => void) | null): void {
  onSessionExpired = handler;
}

// ── 429 retry ─────────────────────────────────────────────────
//
// The auth endpoints are rate limited at 10/minute per IP + email. A transient 429 on the
// calls that DECIDE auth state must never be mistaken for "signed out" — so those, and
// only those, retry.
//
// ⚠ Deliberately not applied to ordinary apiFetch calls: a form submit should surface a
// 429 to the person who pressed the button, not silently stall for nine seconds.

const RETRY_DELAYS_MS: readonly number[] = [3000, 6000];
const MAX_RETRY_AFTER_MS = 10_000;

function retryDelayMs(res: Response, attempt: number): number {
  const retryAfter = Number(res.headers.get("Retry-After"));
  if (Number.isFinite(retryAfter) && retryAfter > 0) {
    return Math.min(retryAfter * 1000, MAX_RETRY_AFTER_MS);
  }
  return RETRY_DELAYS_MS[attempt] ?? MAX_RETRY_AFTER_MS;
}

/** `fetch` that retries a 429 a couple of times, then returns the last response. */
export async function fetchRetrying429(input: string, init?: RequestInit): Promise<Response> {
  let res = await fetch(input, init);
  for (let attempt = 0; res.status === 429 && attempt < RETRY_DELAYS_MS.length; attempt++) {
    await new Promise<void>((resolve) => setTimeout(resolve, retryDelayMs(res, attempt)));
    res = await fetch(input, init);
  }
  return res;
}

// ── The main wrapper ──────────────────────────────────────────

export async function apiFetch<T>(path: string, options: RequestInit = {}): Promise<T> {
  const url = `${API_PREFIX}${path}`;

  const headers: Record<string, string> = { ...(options.headers as Record<string, string>) };
  if (!(options.body instanceof FormData)) headers["Content-Type"] = "application/json";
  if (accessToken) headers["Authorization"] = `Bearer ${accessToken}`;

  let res = await fetch(url, { ...options, headers, credentials: "include" });

  // 401 means the access token is missing, expired or revoked. Try the shared refresh
  // cookie WHETHER OR NOT this tab still holds a token — a tab whose in-memory token was
  // dropped by a transient failure recovers here instead of 401-ing forever.
  if (res.status === 401) {
    const outcome = await silentRefresh();
    if (outcome === "refreshed" && accessToken) {
      headers["Authorization"] = `Bearer ${accessToken}`;
      res = await fetch(url, { ...options, headers, credentials: "include" });
    } else if (outcome === "unauthenticated") {
      onSessionExpired?.();
    }
  }

  if (!res.ok) {
    const body = (await res.json().catch(() => ({ message: res.statusText }))) as Partial<ApiErrorBody>;
    throw new ApiError(res.status, body.message || res.statusText, body.details);
  }

  return res.json() as Promise<T>;
}

/**
 * Like `apiFetch`, but hands back the raw `Response`.
 *
 * For downloads: the Excel export is an .xlsx body with a filename in its
 * Content-Disposition, not JSON. ⚠ It goes through the same wrapper rather than a bare
 * `fetch` so it inherits the 401 → silent refresh → retry path; a plain link or a
 * naked fetch would simply fail once the 15-minute access token expired, which is most
 * of the time somebody actually spends on this screen.
 */
export async function apiFetchRaw(path: string, options: RequestInit = {}): Promise<Response> {
  const url = `${API_PREFIX}${path}`;
  const headers: Record<string, string> = { ...(options.headers as Record<string, string>) };
  if (accessToken) headers["Authorization"] = `Bearer ${accessToken}`;

  let res = await fetch(url, { ...options, headers, credentials: "include" });

  if (res.status === 401) {
    const outcome = await silentRefresh();
    if (outcome === "refreshed" && accessToken) {
      headers["Authorization"] = `Bearer ${accessToken}`;
      res = await fetch(url, { ...options, headers, credentials: "include" });
    } else if (outcome === "unauthenticated") {
      onSessionExpired?.();
    }
  }

  if (!res.ok) {
    const body = (await res.json().catch(() => ({ message: res.statusText }))) as Partial<ApiErrorBody>;
    throw new ApiError(res.status, body.message || res.statusText, body.details);
  }

  return res;
}

/**
 * Download a file the API generated, keeping the filename it chose.
 *
 * ⚠ The name comes from Content-Disposition, not from the client. It carries the scope
 * and the data date (`order-activation_circet_2026-09-17.xlsx`) because these files get
 * mailed on to subcontractors, and the whole product is about agreeing which day a
 * number belongs to. A client-side name would be one more thing that can disagree.
 */
export async function apiDownload(path: string, fallbackName: string): Promise<void> {
  const res = await apiFetchRaw(path);
  const disposition = res.headers.get("Content-Disposition") ?? "";
  const match = /filename="?([^";]+)"?/i.exec(disposition);
  const filename = match?.[1] ?? fallbackName;

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

/** POST with a temp token — the half-finished sign-in steps, which have no session yet. */
export async function tempFetch<T>(path: string, tempToken: string, body?: unknown): Promise<T> {
  const res = await fetch(`${API_PREFIX}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${tempToken}` },
    body: body ? JSON.stringify(body) : undefined,
    credentials: "include",
  });

  if (!res.ok) {
    const data = (await res.json().catch(() => ({ message: res.statusText }))) as Partial<ApiErrorBody>;
    throw new ApiError(res.status, data.message || res.statusText, data.details);
  }

  return res.json() as Promise<T>;
}

// ── Silent refresh ────────────────────────────────────────────

export type RefreshOutcome =
  | "refreshed" // a new access token is stored
  | "conflict" // the cookie belongs to a different identity than this tab's pin
  | "unauthenticated" // the origin answered 401: no usable refresh cookie
  | "error"; // 429 after retries, 5xx, network, or a malformed body

// Single-flight: N parallel 401s (several fetches on one screen with an expired token)
// share one refresh, one outcome and one handler call.
let refreshInFlight: Promise<RefreshOutcome> | null = null;

export function silentRefresh(): Promise<RefreshOutcome> {
  if (!refreshInFlight) {
    refreshInFlight = doSilentRefresh().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

async function doSilentRefresh(): Promise<RefreshOutcome> {
  let res: Response;
  try {
    res = await fetchRetrying429(`${API_PREFIX}/auth/refresh`, {
      method: "POST",
      credentials: "include",
    });
  } catch {
    accessToken = null;
    return "error";
  }

  // The route only ever answers 200 or 401 from the app itself. Anything else is
  // infrastructure (nginx 429/502/504) and says nothing at all about the session.
  if (res.status === 401) {
    accessToken = null;
    return "unauthenticated";
  }
  if (!res.ok) {
    accessToken = null;
    return "error";
  }

  let data: Partial<RefreshResponse>;
  try {
    data = (await res.json()) as Partial<RefreshResponse>;
  } catch {
    accessToken = null;
    return "error";
  }
  if (typeof data.accessToken !== "string") {
    accessToken = null;
    return "error";
  }

  // The identity guard. If the refreshed token belongs to someone other than this tab is
  // pinned to, the shared cookie was overwritten by a sign-in elsewhere — refuse it.
  const pinned = getPinnedSessionUser();
  const sub = decodeJwtSub(data.accessToken);
  if (pinned && sub && pinned !== sub) {
    accessToken = null;
    onSessionConflict?.();
    return "conflict";
  }

  accessToken = data.accessToken;
  return "refreshed";
}
